import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Sparkles,
  Globe,
  TrendingUp,
  PhoneOff,
  EyeOff,
  UserX,
  ScanSearch,
  Bot,
  BarChart3,
  Zap,
  Users,
  ArrowRight,
  Play,
} from 'lucide-react';
import { LeadCaptureForm } from '../components/landing/LeadCaptureForm';
import { Seo } from '../components/Seo';
import { softwareApplicationJsonLd } from '../utils/structured-data';
import { config } from '../config';

interface DemoProperty {
  slug: string;
  name: string;
  type: string;
  website: string;
}

const TYPE_LABELS: Record<string, string> = {
  hotel: 'Hotel',
  resort: 'Resort',
  boutique: 'Boutique Hotel',
  vacation_rental: 'Vacation Rental',
  custom: 'Property',
};

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' as const },
  transition: { duration: 0.5 },
};

export function LandingPage() {
  const [demoProperties, setDemoProperties] = useState<DemoProperty[]>([]);

  useEffect(() => {
    fetch(`${config.apiUrl}/v1/demo`)
      .then((res) => (res.ok ? res.json() : { properties: [] }))
      .then((data) => setDemoProperties(data.properties ?? []))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 relative overflow-hidden">
      <Seo
        title="LasoXP — AI Guest Experience Demo"
        description="See LasoXP in action. Enter any hotel website URL and watch our AI agent instantly learn and assist guests with amenities, dining, activities, and more."
        canonical="/"
        jsonLd={softwareApplicationJsonLd}
      />
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-to-r from-lasoxp-200/30 to-accent-200/30 rounded-full blur-3xl animate-float" />
        <div
          className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-gradient-to-r from-accent-200/20 to-lasoxp-200/20 rounded-full blur-3xl animate-float"
          style={{ animationDelay: '3s' }}
        />
      </div>

      <div className="relative z-10">
        {/* ─── HERO ─── */}
        <section className="min-h-[92vh] flex items-center justify-center px-6 py-16">
          <div className="max-w-6xl w-full grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left: Copy + Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="flex items-center gap-3 mb-8">
                <div className="w-9 h-9 bg-gradient-to-br from-lasoxp-500 to-accent-600 rounded-xl flex items-center justify-center shadow-glow">
                  <Sparkles className="w-4.5 h-4.5 text-white" />
                </div>
                <span className="text-sm font-display font-semibold text-lasoxp-700 tracking-wide uppercase">
                  LasoXP
                </span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-display font-bold text-gray-900 leading-[1.1] mb-6">
                Your guests have questions.
                <br />
                <span className="bg-gradient-to-r from-lasoxp-600 to-accent-600 bg-clip-text text-transparent">
                  Your website should answer them.
                </span>
              </h1>

              <p className="text-lg text-gray-600 leading-relaxed mb-4 max-w-lg">
                An AI concierge that learns everything about your property and
                answers guest questions on your website — 24/7. Every
                conversation becomes a guest profile you can act on.
              </p>

              {/* Social proof nudge */}
              <p className="text-sm text-gray-400 mb-8 flex items-center gap-2">
                <span className="flex -space-x-1">
                  {[...Array(3)].map((_, i) => (
                    <span
                      key={i}
                      className="w-5 h-5 rounded-full bg-gradient-to-br from-lasoxp-300 to-accent-300 border-2 border-white inline-block"
                    />
                  ))}
                </span>
                Live on resort, boutique, and luxury properties
              </p>

              <LeadCaptureForm variant="hero" />

              {/* Escape hatch for browsers */}
              {demoProperties.length > 0 && (
                <div className="mt-5 flex items-center gap-2 text-sm text-gray-500">
                  <Play className="w-3.5 h-3.5 text-lasoxp-500" />
                  <span>
                    Or{' '}
                    <a
                      href="#demos"
                      className="text-lasoxp-600 font-semibold hover:text-lasoxp-700 underline underline-offset-2"
                    >
                      try a live demo
                    </a>{' '}
                    on a real hotel website first
                  </span>
                </div>
              )}
            </motion.div>

            {/* Right: Widget Mockup */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="hidden lg:block"
            >
              <div className="relative">
                {/* Browser frame */}
                <div className="bg-white rounded-2xl shadow-large border border-gray-200 overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2.5 flex items-center gap-2 border-b border-gray-200">
                    <div className="flex gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                      <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                      <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
                    </div>
                    <div className="flex-1 bg-white rounded-md px-3 py-1 text-[11px] text-gray-400 font-mono mx-2 border border-gray-100">
                      www.grandresort.com
                    </div>
                  </div>

                  {/* Fake hotel page content */}
                  <div className="p-5 bg-gradient-to-b from-stone-50 to-white h-80 relative">
                    <div className="flex gap-4 mb-4 text-[10px] font-medium text-gray-400 uppercase tracking-wider">
                      <span>Rooms</span>
                      <span>Dining</span>
                      <span>Spa</span>
                      <span>Events</span>
                    </div>
                    <div className="h-3 bg-gray-200/80 rounded w-2/3 mb-2" />
                    <div className="h-2.5 bg-gray-100 rounded w-full mb-1.5" />
                    <div className="h-2.5 bg-gray-100 rounded w-4/5 mb-4" />
                    <div className="h-24 bg-stone-100 rounded-lg w-full mb-3" />
                    <div className="flex gap-2">
                      <div className="h-12 bg-stone-100 rounded-lg flex-1" />
                      <div className="h-12 bg-stone-100 rounded-lg flex-1" />
                    </div>

                    {/* Chat widget conversation */}
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ delay: 1.2, duration: 0.5 }}
                      className="absolute bottom-3 right-3 w-[260px]"
                    >
                      <div className="bg-white rounded-2xl shadow-large border border-gray-200 overflow-hidden">
                        {/* Chat header */}
                        <div className="bg-gradient-to-r from-lasoxp-600 to-accent-600 px-3.5 py-2.5 flex items-center gap-2">
                          <div className="w-6 h-6 bg-white/20 rounded-lg flex items-center justify-center">
                            <Sparkles className="w-3 h-3 text-white" />
                          </div>
                          <div>
                            <p className="text-[11px] font-semibold text-white">Grand Resort</p>
                            <p className="text-[9px] text-white/70">AI Concierge</p>
                          </div>
                        </div>
                        {/* Messages */}
                        <div className="p-3 space-y-2.5 bg-gray-50/50">
                          <div className="bg-white rounded-xl rounded-tl-sm px-3 py-2 shadow-sm border border-gray-100 max-w-[85%]">
                            <p className="text-[11px] text-gray-700 leading-relaxed">
                              Welcome! I know everything about our resort. What can I help you with?
                            </p>
                          </div>
                          <div className="bg-lasoxp-600 rounded-xl rounded-tr-sm px-3 py-2 ml-auto max-w-[80%]">
                            <p className="text-[11px] text-white leading-relaxed">
                              Do you have a pool? And what&apos;s check-in time?
                            </p>
                          </div>
                          <div className="bg-white rounded-xl rounded-tl-sm px-3 py-2 shadow-sm border border-gray-100 max-w-[90%]">
                            <p className="text-[11px] text-gray-700 leading-relaxed">
                              Yes! Our heated infinity pool is open 7am-10pm. Check-in is at 3pm, but early arrival is available upon request.
                            </p>
                          </div>
                        </div>
                        {/* Suggestions */}
                        <div className="px-3 pb-2.5 flex gap-1 flex-wrap">
                          {['Room types', 'Spa menu', 'Dining'].map((s) => (
                            <span
                              key={s}
                              className="text-[9px] bg-lasoxp-50 text-lasoxp-700 px-2 py-0.5 rounded-full font-medium"
                            >
                              {s}
                            </span>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </div>

                {/* Floating badge */}
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.8, duration: 0.4 }}
                  className="absolute -left-4 top-1/3 bg-white rounded-xl shadow-medium border border-gray-100 px-3 py-2"
                >
                  <p className="text-[10px] font-semibold text-gray-800">Guest profile created</p>
                  <p className="text-[9px] text-gray-400">Interested in: pool, early check-in</p>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ─── LIVE DEMOS (proof before pitch) ─── */}
        {demoProperties.length > 0 && (
          <section id="demos" className="py-16 px-6 bg-white/40 scroll-mt-8">
            <div className="max-w-4xl mx-auto">
              <motion.div {...fadeInUp} className="text-center mb-10">
                <p className="text-sm font-semibold text-lasoxp-600 uppercase tracking-wider mb-2">
                  Don&apos;t take our word for it
                </p>
                <h2 className="text-3xl sm:text-4xl font-display font-bold text-gray-900 mb-3">
                  Try it live on a real hotel website
                </h2>
                <p className="text-gray-500 max-w-lg mx-auto">
                  Click any property below to see the AI concierge in action —
                  ask it anything about the hotel.
                </p>
              </motion.div>

              <div className={`grid grid-cols-1 gap-4 ${demoProperties.length === 1 ? 'max-w-sm mx-auto' : demoProperties.length === 2 ? 'sm:grid-cols-2 max-w-2xl mx-auto' : 'sm:grid-cols-3'}`}>
                {demoProperties.map((property, i) => (
                  <motion.div
                    key={property.slug}
                    {...fadeInUp}
                    transition={{ duration: 0.4, delay: i * 0.08 }}
                  >
                    <Link
                      to={`/${property.slug}`}
                      className="group block bg-white rounded-2xl border border-gray-200 p-5 shadow-soft hover:shadow-medium hover:border-lasoxp-300 transition-all duration-300"
                    >
                      <div className="flex items-center gap-3 mb-2.5">
                        <div className="w-9 h-9 bg-gradient-to-br from-lasoxp-100 to-accent-100 rounded-xl flex items-center justify-center group-hover:from-lasoxp-200 group-hover:to-accent-200 transition-colors">
                          <Globe className="w-4.5 h-4.5 text-lasoxp-600" />
                        </div>
                        <div>
                          <h3 className="font-display font-bold text-gray-900 text-sm group-hover:text-lasoxp-700 transition-colors">
                            {property.name}
                          </h3>
                          <p className="text-xs text-gray-400">{TYPE_LABELS[property.type] ?? property.type}</p>
                        </div>
                      </div>
                      <span className="text-xs font-semibold text-lasoxp-600 flex items-center gap-1 group-hover:gap-2 transition-all">
                        Open live demo
                        <ArrowRight className="w-3.5 h-3.5" />
                      </span>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ─── PAIN POINTS ─── */}
        <section className="py-20 px-6">
          <div className="max-w-5xl mx-auto">
            <motion.div {...fadeInUp} className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-display font-bold text-gray-900 mb-3">
                Your website is your biggest sales channel.
                <br className="hidden sm:block" />
                <span className="text-gray-400">Is it actually selling?</span>
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                {
                  icon: UserX,
                  title: 'Guests leave without booking',
                  desc: 'They browse after hours, can\'t find answers, and book somewhere else. You never even know they were there.',
                  iconColor: 'text-red-500',
                  bg: 'bg-red-50',
                },
                {
                  icon: PhoneOff,
                  title: 'Staff drowning in repeat questions',
                  desc: '"What time is check-in?" "Is breakfast included?" "Do you have a pool?" The same questions, hundreds of times a month.',
                  iconColor: 'text-amber-500',
                  bg: 'bg-amber-50',
                },
                {
                  icon: EyeOff,
                  title: 'Zero visibility into guest intent',
                  desc: 'People research your property online but you have no idea what they wanted, what they cared about, or why they didn\'t convert.',
                  iconColor: 'text-violet-500',
                  bg: 'bg-violet-50',
                },
              ].map((pain, i) => (
                <motion.div
                  key={pain.title}
                  {...fadeInUp}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-100 p-7 shadow-soft"
                >
                  <div className={`w-11 h-11 ${pain.bg} rounded-xl flex items-center justify-center mb-4`}>
                    <pain.icon className={`w-5 h-5 ${pain.iconColor}`} />
                  </div>
                  <h3 className="text-base font-display font-bold text-gray-900 mb-2">
                    {pain.title}
                  </h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{pain.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── HOW IT WORKS ─── */}
        <section className="py-20 px-6 bg-white/50">
          <div className="max-w-4xl mx-auto">
            <motion.div {...fadeInUp} className="text-center mb-14">
              <p className="text-sm font-semibold text-lasoxp-600 uppercase tracking-wider mb-2">
                Three steps. Five minutes.
              </p>
              <h2 className="text-3xl sm:text-4xl font-display font-bold text-gray-900">
                How guest activation works
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
              {/* Connector lines (desktop only) */}
              <div className="hidden md:block absolute top-8 left-[calc(33.33%+0.5rem)] right-[calc(33.33%+0.5rem)] h-px bg-gradient-to-r from-lasoxp-300 via-accent-300 to-lasoxp-300" />

              {[
                {
                  step: 1,
                  icon: ScanSearch,
                  title: 'We scan your website',
                  desc: 'AI reads your rooms, dining, spa, activities, and policies — building a complete knowledge base from your existing content.',
                },
                {
                  step: 2,
                  icon: Bot,
                  title: 'Your AI concierge goes live',
                  desc: 'A chat widget appears on your site that answers guest questions accurately in your brand voice. Always on, never sick.',
                },
                {
                  step: 3,
                  icon: BarChart3,
                  title: 'Every chat becomes intelligence',
                  desc: 'Guest profiles are created automatically — interests, travel dates, intent signals. Your team knows exactly who\'s ready to book.',
                },
              ].map((item, i) => (
                <motion.div
                  key={item.step}
                  {...fadeInUp}
                  transition={{ duration: 0.5, delay: i * 0.12 }}
                  className="relative text-center bg-white/70 backdrop-blur-sm rounded-2xl border border-gray-100 p-6 pt-10"
                >
                  <div className="absolute -top-5 left-1/2 -translate-x-1/2 w-10 h-10 bg-gradient-to-br from-lasoxp-500 to-accent-600 rounded-xl flex items-center justify-center shadow-glow text-white font-display font-bold text-sm">
                    {item.step}
                  </div>
                  <item.icon className="w-8 h-8 text-lasoxp-600/70 mx-auto mb-3" />
                  <h3 className="text-base font-display font-bold text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── BENEFITS ─── */}
        <section className="py-20 px-6">
          <div className="max-w-5xl mx-auto">
            <motion.div {...fadeInUp} className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-display font-bold text-gray-900 mb-3">
                What changes when your website can talk
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {[
                {
                  icon: TrendingUp,
                  title: 'More direct bookings',
                  desc: 'Guests get instant answers and book direct — instead of bouncing to an OTA or calling tomorrow.',
                  iconColor: 'text-emerald-600',
                  bg: 'bg-emerald-50',
                },
                {
                  icon: PhoneOff,
                  title: 'Fewer repetitive calls',
                  desc: 'Check-in time, parking, pool hours, pet policy — the widget handles FAQs so your team focuses on guests in front of them.',
                  iconColor: 'text-blue-600',
                  bg: 'bg-blue-50',
                },
                {
                  icon: Users,
                  title: 'Guest profiles from day one',
                  desc: 'Every conversation captures interests, preferences, and contact info. Build your guest CRM without lifting a finger.',
                  iconColor: 'text-violet-600',
                  bg: 'bg-violet-50',
                },
                {
                  icon: Zap,
                  title: 'Live in minutes, not months',
                  desc: 'One script tag. No PMS integration needed to start. No IT department required. Works with any hotel website.',
                  iconColor: 'text-amber-600',
                  bg: 'bg-amber-50',
                },
              ].map((benefit, i) => (
                <motion.div
                  key={benefit.title}
                  {...fadeInUp}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-100 p-6 shadow-soft hover:shadow-medium transition-shadow duration-300 flex gap-4"
                >
                  <div className={`w-10 h-10 ${benefit.bg} rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5`}>
                    <benefit.icon className={`w-5 h-5 ${benefit.iconColor}`} />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-gray-900 mb-1">
                      {benefit.title}
                    </h3>
                    <p className="text-sm text-gray-500 leading-relaxed">
                      {benefit.desc}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── BOTTOM CTA ─── */}
        <section className="py-20 px-6">
          <div className="max-w-xl mx-auto">
            <motion.div
              {...fadeInUp}
              className="bg-white/80 backdrop-blur-2xl rounded-3xl border border-white/60 p-8 md:p-12 shadow-large relative"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-lasoxp-50/50 via-white/30 to-accent-50/50 rounded-3xl pointer-events-none" />
              <div className="relative z-10">
                <LeadCaptureForm
                  heading="Ready to see it on your site?"
                  subheading="Enter your hotel's website and we'll build a personalized demo — free, no strings."
                  variant="full"
                />
              </div>
            </motion.div>
          </div>
        </section>

        {/* ─── FOOTER ─── */}
        <footer className="py-8 px-6 border-t border-gray-200/40">
          <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-400">
            <div className="flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5 text-lasoxp-500" />
              <span className="font-display font-semibold text-gray-500">
                LasoXP
              </span>
            </div>
            <p>&copy; {new Date().getFullYear()} LasoXP. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </div>
  );
}
